import React, {FC, useEffect} from "react";
import {useTranslation} from "react-i18next";
import QuestionCard from "../components/QuestionCard";
import ScrollToTopButton from "../components/ScrollToTopButton";
import {IGpsrQuestion} from "../types/gpsr-questions";
import {useQuestions} from "../hooks/useQuestions";
import ArrowLeftIcon from "../components/Icons/ArrowLeftIcon";
import {useNavigate, useParams} from "react-router-dom";
import GpsrNotSavedFormModal from "../components/GpsrNotSavedFormModal";
import LoadingScreen from "../components/LoadingScreen";
import {useAppDispatch} from "../hooks/redux";
import {resetRiskQuestions} from "../store/slices/gpsrQuestionsListSlice";
import GpsrDeleteProductFileModal from "../components/GpsrDeleteProductFileModal";

interface RouteParams {
    productID?: string;
    [key: string]: string | undefined;
}

const FormQuestionsPage: FC = () => {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    let {productID} = useParams<RouteParams>();
    const {
        questions,
        textareaChange,
        fileInputChange,
        deleteFileHandle,
        confirmDeleteFileHandle,
        currentQuestionIDS,
        saveOrUpdateProductHandle,
        getProductID,
        productName,
        currentEditProductName,
        setProductName,
        riskQuestions,
        isSendDataLoading,
        currentFileName,
        currentOriginalFileName,
        open,
        setOpen,
        openDeleteFileModal,
        setOpenDeleteFileModal,
        goBackHandler,
    } = useQuestions(productID ?? "");

    useEffect(() => {
        if (productID) getProductID(productID).finally()
    }, [productID]);

    useEffect(() => {
        return () => {
            dispatch(resetRiskQuestions())
        }
    }, [])

    return (
        <>
            {isSendDataLoading && <LoadingScreen/>}
            <GpsrDeleteProductFileModal
                open={openDeleteFileModal}
                onClose={() => setOpenDeleteFileModal(false)}
                currentQuestionIDs={currentQuestionIDS}
                fileName={currentFileName}
                originalFileName={currentOriginalFileName}
                deleteFileHandle={deleteFileHandle}
            />
            <GpsrNotSavedFormModal
                open={open}
                onClose={() => setOpen(false)}
                onCancel={() => {
                    dispatch(resetRiskQuestions())
                    navigate(-1)
                }}
            />
            <div className="gpsr-content-header-wrapper">
                <div className="gpsr-content-header-title">
                    <button onClick={goBackHandler}>
                        <ArrowLeftIcon/>
                    </button>

                    {!productID
                        ? t("gpsr:add_product_title")
                        : t("gpsr:edit_product_title", {product: currentEditProductName})}
                </div>
                <div className="gpsr-content-header-btn">
                    <button className="btn btn-primary" onClick={() => saveOrUpdateProductHandle(productID ?? null)}>
                        {productID ? t("gpsr:update_product_btn") : t("gpsr:save_product_btn")}
                    </button>
                </div>
            </div>

            <div className="table-container">
                <div className="create-product-name-wrapper">
                    <div className="create-product-section-title">
                        {t("gpsr:create_product_name_title")}
                    </div>
                    <div className="create-product-section-input">
                        <label>
                            {t("gpsr:create_product_label_title")}
                            <span>*</span>
                        </label>
                        <input
                            type="text"
                            value={productName}
                            onChange={(e) => setProductName(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="table-container mt-24">
                <div className="questions-form-title justify-center">
                    {t("gpsr:questions_form_title")}
                </div>
                {questions.map((topic: IGpsrQuestion) => (
                    <QuestionCard
                        key={topic.topicID}
                        topic={topic}
                        textareaChange={textareaChange}
                        fileInputChange={fileInputChange}
                        confirmDeleteFileHandle={confirmDeleteFileHandle}
                    />
                ))}
            </div>

            <ScrollToTopButton/>
        </>
    );
};

export default FormQuestionsPage;
